/**
 * The Chart.js option fragments the two Verlauf cards share: the value axis ticks in the figure's own
 * unit, the chosen-period shading annotation, and the short change animation. Kept in one place so the
 * line card ({@link MiraTrendCardComponent}) and the stacked card ({@link MiraStackedTrendCardComponent})
 * cannot drift apart: a tick or a shaded period must read the same on both.
 *
 * Pure config builders, no Vuetify and no vue-i18n: the colour and the formatter come in from the card.
 */

/** The span of points inside the chosen period, as {@link pointsInRange} returns it. */
export interface MiraTrendSpan {
  first: number;
  last: number;
}

/** A short transition on a real data change (a grain switch, another figure); never replayed per render. */
export const MIRA_TREND_ANIMATION = { duration: 400 };

/**
 * A figure's value formatter. It may also name the step its chart axis counts in, in the value's own unit:
 * an hours duration is stored in days, so it names one hour (1/24) and the axis ticks fall on whole hours.
 */
export type MiraValueFormatter = ((value: number) => string) & { axisUnit?: number };

/** The tick steps an {@link MiraValueFormatter.axisUnit} axis may use, in that unit (hours: 1 h up to a week). */
const AXIS_UNIT_STEPS = [1, 2, 3, 4, 6, 12, 24, 48, 72, 168];

/** The most intervals such an axis draws: fewer, larger steps read better than a crowded scale. */
const AXIS_UNIT_MAX_INTERVALS = 6;

/** The part of a Chart.js linear axis {@link unitAxisTicks} reads and sets. */
export interface MiraUnitAxis {
  getMinMax(canStack: boolean): { min: number; max: number };
  min: number;
  max: number;
  ticks: Array<{ value: number }>;
}

/**
 * Puts an axis's ticks on whole multiples of a unit (whole hours), from zero (or the lowest value) to the
 * first step at or above the highest value, and fits the axis to them. Chart.js would pick round numbers of
 * days instead (0,1 Tage = 2,4 Std.), which an hours label could only show rounded, so off its position.
 */
export function unitAxisTicks(axis: MiraUnitAxis, unit: number): void {
  const { min, max } = axis.getMinMax(true);
  const low = Math.min(0, min) / unit;
  const high = Math.max(0, max) / unit;
  const span = high - low;
  if (!(span > 0)) {
    return;
  }
  const largest = AXIS_UNIT_STEPS[AXIS_UNIT_STEPS.length - 1];
  const step =
    AXIS_UNIT_STEPS.find((candidate) => span / candidate <= AXIS_UNIT_MAX_INTERVALS) ??
    Math.ceil(span / AXIS_UNIT_MAX_INTERVALS / largest) * largest;
  const first = Math.floor(low / step) * step;
  const last = Math.ceil(high / step) * step;
  const ticks: Array<{ value: number }> = [];
  for (let index = 0; first + index * step <= last; index++) {
    ticks.push({ value: (first + index * step) * unit });
  }
  axis.ticks = ticks;
  axis.min = first * unit;
  axis.max = last * unit;
}

/**
 * The value axis options in the figure's own unit, so a share reads "20 %" and not "0.2", and an hours
 * duration ticks on whole hours ({@link unitAxisTicks}). Spread into the card's `y` scale.
 */
export function trendValueAxis(formatValue: MiraValueFormatter) {
  const unit = formatValue.axisUnit;
  return {
    ticks: { callback: (value: number) => formatValue(Number(value)) },
    ...(unit ? { afterBuildTicks: (axis: MiraUnitAxis) => unitAxisTicks(axis, unit) } : {}),
  };
}

/**
 * The chosen period shaded behind the chart (chartjs-plugin-annotation), under everything including the
 * grid so a see-through band still shows it. `null` where no whole point lies inside the period.
 */
export function trendPeriodAnnotation(span: MiraTrendSpan | null, color: string): Record<string, unknown> {
  if (!span) {
    return {};
  }
  return {
    period: {
      type: "box",
      xMin: span.first - 0.5,
      xMax: span.last + 0.5,
      backgroundColor: color,
      borderWidth: 0,
      drawTime: "beforeDraw",
    },
  };
}

/**
 * The `key` a Mira chart is bound with, from its x-axis labels: the chart is re-created whenever the
 * labels change. vue-chart-3 (2.1.2) replaces the axis labels only when the dataset names and count are
 * unchanged; otherwise it swaps the datasets and keeps the old labels, so a switch from Quartal to Jahr
 * whose five largest entities change order drew the year bars under the quarter labels (Hans, #2610112).
 *
 * @param {string[]} labels - the chart's x-axis labels.
 * @returns {string} a key that changes exactly when the labels do.
 */
export function chartAxisKey(labels: string[]): string {
  return labels.join("|");
}
